import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { useTranslation } from "react-i18next";

import ViewContainer from "../../../components/ViewContainer";
import DashboardGrid from "../../../components/grid/DashboardGrid";
import Panel from "../../../components/grid/Panel";
import FunnelChart from "../../../components/charts/FunnelChart";
import WindowSelect from "./WindowSelect";
import { brand, fonts } from "../../../themes/theme";

import journeyData from "../../../data/pharma/cohortJourney.json";

// Convierte la lista {stage|gap} del JSON al contrato de FunnelChart (cada
// etapa lleva la caida INMEDIATAMENTE anterior).
function buildFunnelRows(raw) {
    const rows = [];
    let pendingGap = null;
    let maxGapPp = 0;
    raw.forEach((entry) => {
        if (entry.gap) {
            pendingGap = entry.gap;
            maxGapPp = Math.max(maxGapPp, entry.gap.pct);
        } else {
            rows.push({ stage: entry, gap: pendingGap });
            pendingGap = null;
        }
    });
    return rows.map(({ stage, gap }) => ({
        // FunnelChart solo tiene un slot de texto por fila (`label`) y otro
        // para la nota del hueco (`note`, mostrada junto al `caidaPp`/`noData`
        // ANTERIOR a la barra) — no un subtitulo bajo el label. El "sub" de
        // cada etapa (p.ej. "Mediana 2.3-6.4 anos tras el inicio") se anexa
        // al label para no perder esa anotacion.
        label: stage.sub ? `${stage.label} — ${stage.sub}` : stage.label,
        note: gap ? gap.note : stage.note,
        retentionPct: stage.retentionPct,
        count: stage.count,
        caidaPp: gap ? gap.pct : null,
        critical: gap ? gap.pct === maxGapPp : false,
        noData: !gap && stage.note != null,
    }));
}

// Modulo 2.2 — Cohort · Journey. Tanto Cohort como Window son funcionales
// aqui (a diferencia de Cohort Overview): los 3 cortes de cohorte y las 3
// ventanas se derivan de las cifras en cohortJourney.json (funnelByCohort
// esta indexado por [cohortId][windowId]).
function CohortJourneyView() {
    const { t } = useTranslation();
    const [cohortId, setCohortId] = useState("all");
    const [windowId, setWindowId] = useState("24");
    const [openGroupKey, setOpenGroupKey] = useState(journeyData.unmetNeeds[0]?.key || null);
    const funnelRows = buildFunnelRows(journeyData.funnelByCohort[cohortId][windowId]);

    return (
        <ViewContainer title={t("views.pharmaCohortJourney.title")} description={t("views.pharmaCohortJourney.description")}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5, mb: 2 }}>
                <WindowSelect tPrefix="views.pharmaCohortJourney" value={windowId} onChange={setWindowId} />
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{t("views.pharmaCohortJourney.filters.cohortLabel")}:</Typography>
                    <Box
                        component="select"
                        aria-label={t("views.pharmaCohortJourney.filters.cohortLabel")}
                        value={cohortId}
                        onChange={(e) => setCohortId(e.target.value)}
                        sx={{
                            p: "5px 9px",
                            bgcolor: "#fff",
                            border: "1px solid",
                            borderColor: brand.borderLight,
                            borderRadius: "7px",
                            fontSize: 12,
                            color: "text.primary",
                            fontFamily: fonts.body,
                            cursor: "pointer",
                            outline: "none",
                            "&:focus": { borderColor: "primary.main" },
                        }}
                    >
                        {journeyData.cohortOptions.map((opt) => (
                            <option key={opt.id} value={opt.id}>{opt.label}</option>
                        ))}
                    </Box>
                </Box>
            </Box>
            <DashboardGrid gap={2} rowGap={2.5}>
                <Panel span={{ xs: 12, md: 7 }} dense fill sx={{ height: { md: 640 } }} title={t("views.pharmaCohortJourney.journeyTitle")} subtitle={t("views.pharmaCohortJourney.journeyHint")}>
                    <FunnelChart
                        data={funnelRows}
                        barHeight={32}
                        fill
                        dropUnit="%"
                        continuesLabel={t("views.pharmaCohortJourney.noAdditionalLossLabel")}
                    />
                </Panel>

                <Panel span={{ xs: 12, md: 5 }} dense sx={{ height: { md: 640 } }} title={t("views.pharmaCohortJourney.unmetNeedsTitle")} subtitle={t("views.pharmaCohortJourney.unmetNeedsHint")}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, height: { md: "100%" }, overflowY: { md: "auto" } }}>
                        {journeyData.unmetNeeds.map((group) => {
                            const open = openGroupKey === group.key;
                            return (
                                <Box
                                    key={group.key}
                                    sx={{
                                        flexShrink: 0,
                                        borderRadius: "10px",
                                        border: "1px solid",
                                        borderColor: open ? "primary.main" : brand.gray100,
                                        overflow: "hidden",
                                        transition: "border-color 0.2s ease",
                                    }}
                                >
                                    <Box
                                        component="button"
                                        type="button"
                                        onClick={() => setOpenGroupKey(open ? null : group.key)}
                                        sx={{
                                            width: "100%",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            gap: 1,
                                            px: 1.5,
                                            py: 1,
                                            border: 0,
                                            bgcolor: open ? "rgba(50,72,202,0.045)" : "transparent",
                                            cursor: "pointer",
                                            textAlign: "left",
                                            fontFamily: "inherit",
                                            transition: "background-color 0.2s ease",
                                            "&:hover": { bgcolor: open ? "rgba(50,72,202,0.06)" : brand.gray50 },
                                        }}
                                    >
                                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: open ? "primary.main" : "text.primary" }}>
                                            {group.title}
                                        </Typography>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                            <Typography sx={{ fontSize: 10.5, color: "text.secondary" }}>{group.items.length}</Typography>
                                            <ExpandMoreRoundedIcon
                                                sx={{
                                                    fontSize: 18,
                                                    color: open ? "primary.main" : "text.secondary",
                                                    transform: open ? "rotate(180deg)" : "rotate(0deg)",
                                                    transition: "transform 0.25s ease",
                                                }}
                                            />
                                        </Box>
                                    </Box>

                                    <Box sx={{ display: "grid", gridTemplateRows: open ? "1fr" : "0fr", transition: "grid-template-rows 0.28s ease" }}>
                                        <Box sx={{ overflow: "hidden", minHeight: 0 }}>
                                            <Box sx={{ px: 1.5, pb: 1, pt: 0.25 }}>
                                                {group.items.map((item, i) => {
                                                    const barColor = item.pct >= 35 ? brand.accent : item.pct >= 22 ? brand.accentMedium : brand.primaryLight;
                                                    return (
                                                        <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5, borderBottom: "1px solid", borderColor: brand.gray100, "&:last-of-type": { borderBottom: "none" } }}>
                                                            <Typography sx={{ fontSize: 11.5, color: "text.secondary", lineHeight: 1.35, flex: 1, minWidth: 0 }}>
                                                                {item.label}
                                                            </Typography>
                                                            <Box sx={{ width: 40, height: 5, borderRadius: 3, bgcolor: brand.gray100, overflow: "hidden", flexShrink: 0 }}>
                                                                <Box sx={{ height: "100%", width: `${item.pct}%`, bgcolor: barColor, borderRadius: 3 }} />
                                                            </Box>
                                                            <Typography sx={{ fontSize: 12, fontWeight: 500, color: "text.primary", flexShrink: 0, width: 30, textAlign: "right" }}>
                                                                {item.pct}%
                                                            </Typography>
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </Panel>
            </DashboardGrid>
        </ViewContainer>
    );
}

export default CohortJourneyView;
